import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getErrorMessage, logError } from '../../../utils/errorHandler';
import { getItemImageSrc, handleImgError } from '../../../utils/itemUtils';
import Spinner from '../../Shared/Spinner';
import { useMercenary } from '../../../contexts/MercenaryContext';
import { DragDropContext, Droppable, Draggable, DropResult, DragUpdate, DragStart } from 'react-beautiful-dnd';
import Tooltip from '../ItemTooltip';
import Toast from '../../Shared/Toast';
import { Item } from '../../types/Item';
import RuneSocketPanel, { SocketedRune } from './RuneSocketPanel';
import Modal from '../../Shared/Modal';
import styles from './Inventory.module.css';

interface ActivePotion {
  name: string;
  effect: string;
  effectEndTime: string;
}

interface PotionWithTimeLeft extends ActivePotion {
  hours: number;
  minutes: number;
  seconds: number;
}

interface MercWeight {
  strength: number;
  race: string;
}

const RACE_BASE_WEIGHT: Record<string, number> = {
  goblin: 2, elf: 0, human: 5, dwarf: 6, orc: 7, troll: 12,
};

const Inventory: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<Item[]>([]);
  const [activePotions, setActivePotions] = useState<PotionWithTimeLeft[]>([]);
  const { mercenaryId } = useMercenary();
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [activeTab, setActiveTab] = useState('All');
  const [validDragSlots, setValidDragSlots] = useState<string[]>([]);
  const [mercStats, setMercStats] = useState<MercWeight | null>(null);
  const [socketedRunes, setSocketedRunes] = useState<SocketedRune[]>([]);
  const [draggingRuneColor, setDraggingRuneColor] = useState<string | null>(null);
  const [runeSocketConfirm, setRuneSocketConfirm] = useState<{
    runeName: string;
    runeItemId: number;
    itemInstanceId: number;
    runeSlotIndex: number;
    targetName: string;
    replacingRuneName?: string;
  } | null>(null);

  const executeSocketRune = async (runeItemId: number, itemInstanceId: number, runeSlotIndex: number, replaceExisting = false) => {
    try {
      await axios.post('/api/inventory/socket-rune', {
        mercenaryId, runeItemId, itemInstanceId, runeSlotIndex,
        ...(replaceExisting ? { replaceExisting: true } : {}),
      }, { withCredentials: true });
      await fetchItems(true);
      await fetchSocketedRunes();
      displayToast(replaceExisting ? 'Rune replaced successfully!' : 'Rune socketed permanently!', 'success');
    } catch (err: any) {
      displayToast(err?.response?.data?.message || 'Failed to socket rune.', 'error');
    }
  };

  const displayToast = (message: string, type: 'success' | 'error') => {
    setToastMessage(message);
    setToastType(type);
  };

  const fetchItems = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await axios.get(`/api/inventory/${mercenaryId}`, { withCredentials: true });
      setItems(response.data);
    } catch (error) {
      logError(error, 'Inventory.fetchItems');
      displayToast(getErrorMessage(error, 'Failed to load inventory items'), 'error');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchSocketedRunes = async () => {
    try {
      const res = await axios.get(`/api/inventory/${mercenaryId}/socketed-runes`, { withCredentials: true });
      setSocketedRunes(res.data);
    } catch (error) {
      logError(error, 'Inventory.fetchSocketedRunes');
    }
  };

  const fetchActivePotions = async () => {
    try {
      const response = await axios.get(`/api/inventory/${mercenaryId}/activePotions`, { withCredentials: true });
      const potions = response.data.map((potion: ActivePotion) => calculateTimeLeft(potion));
      setActivePotions(potions);
    } catch (error) {
      logError(error, 'Inventory.fetchActivePotions');
      displayToast(getErrorMessage(error, 'Failed to load active potions'), 'error');
    }
  };

  const calculateTimeLeft = (potion: ActivePotion) => {
    const effectEndTime = new Date(potion.effectEndTime).getTime();
    const currentTime = new Date().getTime();
    const remainingTime = effectEndTime - currentTime;
    
    if (remainingTime <= 0) {
       return { ...potion, hours: 0, minutes: 0, seconds: 0 };
    }

    const hours = Math.floor(remainingTime / (1000 * 60 * 60));
    const minutes = Math.floor((remainingTime % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((remainingTime % (1000 * 60)) / 1000);

    return { ...potion, hours, minutes, seconds };
  };

  useEffect(() => {
    fetchItems();
    fetchActivePotions();
    fetchSocketedRunes();
    // Fetch strength + race for weight display
    if (mercenaryId) {
      axios.get(`/api/mercenary/${mercenaryId}`, { withCredentials: true })
        .then(res => setMercStats({ strength: res.data.strength, race: res.data.race }))
        .catch(() => {});
    }

    const interval = setInterval(() => {
      setActivePotions((prevPotions) => prevPotions.map(calculateTimeLeft).filter(p => (p.hours > 0 || p.minutes > 0 || p.seconds > 0)));
    }, 1000);

    return () => clearInterval(interval);
  }, [mercenaryId]);

  let equipRequestInProgress = false;
  const handleEquip = async (itemInstanceId: number | undefined, itemId: number, slot: string) => {
    if (equipRequestInProgress) return;
    equipRequestInProgress = true;
    try {
      const response = await axios.post(`/api/inventory/equip`, { mercenaryId, itemInstanceId, slot }, { withCredentials: true });
      if (response.status === 200) {
        await fetchItems(true);
        displayToast('Item equipped successfully.', 'success');
      }
    } catch (error: any) {
      logError(error, 'Inventory.handleEquip');
      displayToast(error?.response?.data?.message || 'Failed to equip item.', 'error');
    } finally {
      equipRequestInProgress = false;
    }
  };

  const handleUnequip = async (itemId: number, slot: string) => {
    try {
      const response = await axios.post(`/api/inventory/unequip`, { mercenaryId, itemId, slot }, { withCredentials: true });
      if (response.status === 200) {
        await fetchItems(true);
        displayToast('Item unequipped successfully.', 'success');
      }
    } catch (error) {
      logError(error, 'Inventory.handleUnequip');
      displayToast(getErrorMessage(error, 'Failed to unequip item'), 'error');
    }
  };

  const handleUsePotion = async (itemId: number) => {
    try {
      const response = await axios.post(`/api/inventory/usePotion`, { mercenaryId, itemId }, { withCredentials: true });
      if (response.status === 200) {
        await fetchItems(true);
        await fetchActivePotions();
        displayToast(response.data?.message || 'Potion used successfully.', 'success');
      }
    } catch (error: any) {
      displayToast(error.response?.data?.message || 'Error using potion.', 'error');
    }
  };

  const handleUseKnowledgeItem = async (itemId: number) => {
    try {
      const response = await axios.post(`/api/craft/unlock-blueprint`, { mercenaryId, itemId }, { withCredentials: true });
      if (response.status === 200) {
        const msg = response.data?.message || 'Blueprint added to your study list!';
        displayToast(msg, 'success');
        await fetchItems(true);
      }
    } catch (error: any) {
      const msg = error.response?.data?.message || 'Failed to unlock blueprint.';
      displayToast(msg, 'error');
    }
  };

  /** Find an item from the items array using a draggableId.
   *  Formats: "{itemId}-inst{instanceId}-inventory" | "{itemId}-inventory" | "{itemId}-{slot}" */
  const findDraggedItem = (dragId: string): Item | undefined => {
    const parts = dragId.split('-');
    const itemId = parseInt(parts[0], 10);
    // Instance-aware inventory drag: "{itemId}-inst{instanceId}-inventory"
    if (parts[1]?.startsWith('inst')) {
      const instanceId = parseInt(parts[1].replace('inst', ''), 10);
      return items.find(i => i.id === itemId && i.itemInstanceId === instanceId);
    }
    // Stacked inventory item: "{itemId}-inventory"
    if (parts[1] === 'inventory') {
      return items.find(i => i.id === itemId && !i.equipped);
    }
    // Equipped item: "{itemId}-{slot}"
    if (parts.length >= 2) {
      const slot = parts.slice(1).join('-');
      return items.find(i => i.id === itemId && i.equipped && i.slot === slot);
    }
    return items.find(i => i.id === itemId);
  };

  const onDragStart = (start: DragStart) => {
    const item = findDraggedItem(start.draggableId);
    if (!item) { setValidDragSlots([]); return; }

    // Rune drag, highlight matching empty socket circles
    if (item.itemCategory === 'Rune') {
      setDraggingRuneColor(item.runeColor || null);
      setValidDragSlots([]);
      return;
    }

    if (item.itemCategory === 'Material' || item.itemCategory === 'Knowledge' || item.itemCategory === 'Enchant') {
      setValidDragSlots([]);
      return;
    }
    let slots: string[] = [];
    if (item.itemCategory === 'Weapon') {
      if (item.slot === 'twoHand') {
        slots = ['mainHand'];
      } else {
        slots = ['mainHand'];
        if (item.canEquipInOffHand) slots.push('offHand');
      }
    } else if (item.itemCategory === 'Shield') {
      slots = ['offHand'];
    } else {
      slots = [item.slot];
    }
    setValidDragSlots(slots);
  };

  const onDragEnd = (result: DropResult) => {
    const { destination, source, draggableId } = result;

    setDraggingRuneColor(null);

    if (!destination) {
      setValidDragSlots([]);
      return;
    }
    setValidDragSlots([]);
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    const draggableParts = draggableId.split('-');
    const itemId = parseInt(draggableParts[0], 10);

    const item = findDraggedItem(draggableId);
    if (!item) {
      displayToast('Dragged item not found.', 'error');
      return;
    }

    // ── Rune socket drop ────────────────────────────────────────────────────
    if (destination.droppableId.startsWith('runeSocket-')) {
      if (item.itemCategory !== 'Rune') {
        displayToast('Only runes can be socketed here.', 'error');
        return;
      }
      // droppableId format: runeSocket-{equipmentSlot}-{slotIndex}
      // e.g. runeSocket-mainHand-0  or  runeSocket-twoHand-1
      const parts = destination.droppableId.split('-');
      const slotIndex = parseInt(parts[parts.length - 1], 10);
      const equipmentSlot = parts.slice(1, -1).join('-');

      // Validate color match client-side for immediate feedback
      const equippedItem = items.find(i => i.equipped && i.slot === equipmentSlot);
      const neededColor = equippedItem?.runeSlots?.[slotIndex];
      if (neededColor && neededColor !== item.runeColor) {
        displayToast(`Wrong color, this slot needs a ${neededColor} rune.`, 'error');
        return;
      }

      if (!equippedItem?.itemInstanceId) {
        displayToast('Cannot socket rune: item has no instance ID.', 'error');
        return;
      }

      // Check if this slot already has a rune socketed
      const existingSocketed = socketedRunes.find(
        sr => sr.itemInstanceId === equippedItem.itemInstanceId && sr.runeSlotIndex === slotIndex
      );

      setRuneSocketConfirm({
        runeName: item.name,
        runeItemId: item.id,
        itemInstanceId: equippedItem.itemInstanceId,
        runeSlotIndex: slotIndex,
        targetName: `${equippedItem.name} (${equipmentSlot}, slot ${slotIndex + 1})`,
        ...(existingSocketed ? { replacingRuneName: existingSocketed.rune.name } : {}),
      });
      return;
    }

    // Dropping back into inventory bag
    if (destination.droppableId === 'inventory-bag') {
      if (item.equipped) {
        handleUnequip(item.id, item.slot);
      }
      return;
    }

    // Dropping into an equipment slot
    if (destination.droppableId.startsWith('equipped-')) {
      const destinationSlot = destination.droppableId.replace('equipped-', '');

      // Block non-equippable items from being dragged to equipment slots
      if (['Material', 'Knowledge', 'Enchant', 'Potion'].includes(item.itemCategory)) {
        displayToast('This item cannot be equipped.', 'error');
        return;
      }

      // ── Rune dropped onto a gear piece ──────────────────────────────────
      if (item.itemCategory === 'Rune') {
        const equippedItem = items.find(i => i.equipped && i.slot === destinationSlot);
        if (!equippedItem) {
          displayToast('No item equipped in that slot.', 'error');
          return;
        }
        const runeSlots = (equippedItem.runeSlots as string[]) || [];
        if (runeSlots.length === 0) {
          displayToast('That item has no rune slots.', 'error');
          return;
        }
        // Find first slot matching rune color that isn't already socketed
        let slotIndex = runeSlots.findIndex((color: string, idx: number) =>
          color === item.runeColor &&
          !socketedRunes.find(sr => sr.itemInstanceId === equippedItem.itemInstanceId && sr.runeSlotIndex === idx)
        );
        // If no empty slot, fall back to first matching-color filled slot (replacement)
        let existingSocketedRune: typeof socketedRunes[number] | undefined;
        if (slotIndex === -1) {
          slotIndex = runeSlots.findIndex((color: string, idx: number) => {
            if (color !== item.runeColor) return false;
            existingSocketedRune = socketedRunes.find(sr => sr.itemInstanceId === equippedItem.itemInstanceId && sr.runeSlotIndex === idx);
            return !!existingSocketedRune;
          });
        }
        if (slotIndex === -1) {
          displayToast(`No ${item.runeColor} slot on ${equippedItem.name}.`, 'error');
          return;
        }
        if (!equippedItem?.itemInstanceId) {
          displayToast('Cannot socket rune: item has no instance ID.', 'error');
          return;
        }
        setRuneSocketConfirm({
          runeName: item.name,
          runeItemId: item.id,
          itemInstanceId: equippedItem.itemInstanceId,
          runeSlotIndex: slotIndex,
          targetName: `${equippedItem.name} (${destinationSlot}, slot ${slotIndex + 1})`,
          ...(existingSocketedRune ? { replacingRuneName: existingSocketedRune.rune.name } : {}),
        });
        return;
      }

      // Basic validation
      if (item.itemCategory === 'Weapon') {
        if (item.slot === 'twoHand') {
           // If dragging to mainHand or offHand, force twoHand slot
           handleEquip(item.itemInstanceId, item.id, 'twoHand'); 
        } else if (item.slot === 'oneHand') {
          if (destinationSlot === 'mainHand' || (destinationSlot === 'offHand' && item.canEquipInOffHand)) {
            handleEquip(item.itemInstanceId, item.id, destinationSlot);
          } else if (destinationSlot === item.slot) /* explicit slot match */ {
            handleEquip(item.itemInstanceId, item.id, destinationSlot);
          } else {
             displayToast('Invalid slot for this weapon.', 'error');
          }
        } else {
             handleEquip(item.itemInstanceId, item.id, destinationSlot);
        }
      } else if (item.itemCategory === 'Shield') {
        if (destinationSlot === 'offHand') {
          handleEquip(item.itemInstanceId, item.id, 'offHand');
        } else {
            displayToast('Shields must go in Off Hand.', 'error');
        }
      } else {
        // Armor/Accessories
         // Check if destination slot matches item slot type (simple check)
         // The server validates, but good UI feedback is nice.
         // For now, let the server decide or simple check:
         if (destinationSlot === item.slot || 
             (item.slot === 'Ring' && destinationSlot === 'Armring') || // Assuming logic allows
             (item.slot === 'Neck' && destinationSlot === 'Amulet')     // Assuming logic allows
             ) {
             // Let server handle strict slot validation
             handleEquip(item.itemInstanceId, item.id, destinationSlot);
         } else if (item.slot === destinationSlot) {
             handleEquip(item.itemInstanceId, item.id, destinationSlot);
         } else {
             handleEquip(item.itemInstanceId, item.id, destinationSlot); // Try anyway
         }
      }
    }
  };
  
  const onDragUpdate = (update: DragUpdate) => {
    // visual cues handled via onDragStart/onDragEnd
  };

  const handleDoubleClick = (item: Item) => {
      // Toggle equip/unequip
      if (item.equipped) {
          handleUnequip(item.id, item.slot);
      } else {
          // Auto-equip logic
           if (item.itemCategory === 'Potion') {
             handleUsePotion(item.id);
             return;
           }
           if (item.itemCategory === 'Knowledge') {
               handleUseKnowledgeItem(item.id);
               return;
           }
           if (['Material', 'Knowledge', 'Enchant'].includes(item.itemCategory)) return;

           // Try to equip to default slot
           let targetSlot = item.slot;
           if (item.itemCategory === 'Weapon' && item.slot === 'oneHand') {
               // Always target mainHand on double-click (swap if occupied).
               // Offhand is intentional and should only be done via drag-and-drop.
               targetSlot = 'mainHand';
           }
           if (item.itemCategory === 'Weapon' && item.slot === 'twoHand') {
               targetSlot = 'twoHand';
           }
           if (item.itemCategory === 'Shield') targetSlot = 'offHand';

           handleEquip(item.itemInstanceId, item.id, targetSlot);
      }
  };

  // --- Render Helpers ---

  // Visual layout of slots for the Paper Doll
  const paperDollSlots = [
      { id: 'Head',       css: styles.slotHead,      label: 'Head' },
      { id: 'Neck',       css: styles.slotNeck,      label: 'Neck' },
      { id: 'Amulet',     css: styles.slotAmulet,    label: 'Amulet' },
      { id: 'Shoulders',  css: styles.slotShoulders, label: 'Shoulders' },
      { id: 'Torso',      css: styles.slotTorso,     label: 'Torso' },
      { id: 'Cloak',      css: styles.slotCloak,     label: 'Cloak' },
      { id: 'Hands',      css: styles.slotHands,     label: 'Hands' },
      { id: 'mainHand',   css: styles.slotMainHand,  label: 'Main Hand' },
      { id: 'offHand',    css: styles.slotOffHand,   label: 'Off Hand' },
      { id: 'Legs',       css: styles.slotLegs,      label: 'Legs' },
      { id: 'Feet',       css: styles.slotFeet,      label: 'Feet' },
      { id: 'Armring',    css: styles.slotArmring,   label: 'Armring' },
      { id: 'Ring',       css: styles.slotRing,      label: 'Ring' },
  ];

  const getBagItems = () => {
    return items.filter(item => {
        if (item.equipped) return false;
        if (activeTab === 'All') return true;
        if (activeTab === 'Gear') {
            return ['Weapon', 'Armor', 'Shield', 'Accessory'].includes(item.itemCategory);
        }
        if (activeTab === 'Consumables') {
             return ['Potion', 'Food', 'Rune'].includes(item.itemCategory);
        }
        if (activeTab === 'Materials') {
             return ['Material', 'Knowledge', 'Enchant'].includes(item.itemCategory);
        }
        return true;
    });
  };

  // Whether any equipped item has rune slots (determines connected panel style)
  const hasRunePanel = items.some(i => i.equipped && Array.isArray(i.runeSlots) && (i.runeSlots as string[]).length > 0);

  const getRarityClass = (item: Item) => {
       // Assuming rarity isn't in item but could be inferred or standard
       // For now, return standard based on some prop or random if not present
       // Not implemented in Item interface shown, so defaulting.
       return styles.rarityCommon; 
  };
  
  const bagItems = getBagItems();

  const equippedGearWeight = items.filter(i => i.equipped).reduce((s, i) => s + (i.weight ?? 0), 0);
  const raceW = RACE_BASE_WEIGHT[(mercStats?.race ?? '').toLowerCase()] ?? 0;
  const rawWeight = equippedGearWeight + raceW;
  const strMitigation = Math.min((mercStats?.strength ?? 0) / 10, 25);
  const effectiveWeight = Math.max(0, rawWeight - strMitigation);

  return (
    <div className={styles.inventoryPage}>
         {toastMessage && (
            <Toast
              message={toastMessage}
              type={toastType}
              duration={3000}
              onClose={() => setToastMessage(null)}
            />
          )}

        <div className={styles.headerSection}>
          <h1 className={styles.title}>Mercenary Inventory</h1>
        </div>

        {loading ? <Spinner /> : (
            <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd} onDragUpdate={onDragUpdate}>
                <div className={styles.mainContainer}>

                    {/* LEFT GROUP: Rune Panel + Character Sheet (visually connected) */}
                    <div className={styles.equipmentGroup}>

                    {/* LEFTMOST: Rune Socket Panel (only visible when gear has rune slots) */}
                    <RuneSocketPanel
                      items={items}
                      socketedRunes={socketedRunes}
                      draggingRuneColor={draggingRuneColor}
                    />

                    {/* MIDDLE: Character Sheet */}
                    <div className={`${styles.characterSheet} ${hasRunePanel ? styles.characterSheetRight : ''}`}>
                        <div className={styles.characterHeader}>
                            <h2 className={styles.characterName}>Equipment</h2>
                            <div className={styles.weightBadge}>
                              <span className={styles.weightBadgeLabel}>Weight </span>
                              <span className={styles.weightBadgeVal}>{rawWeight}</span>
                              <span className={styles.weightBadgeLabel}> | After Str </span>
                              <span className={styles.weightBadgeVal}>{effectiveWeight.toFixed(1)}</span>
                            </div>
                        </div>
                        
                        <div className={styles.paperDollContainer}>
                            {paperDollSlots.map(slotDef => {
                                // Find item in this slot
                                const equippedItem = items.find(i => i.equipped && i.slot === slotDef.id);
                                // Handle twoHand taking up offhand visually if we want, or just empty
                                const twoHandItem = items.find(i => i.equipped && i.slot === 'twoHand');
                                const displayItem = equippedItem || (slotDef.id === 'mainHand' ? twoHandItem : null);
                                // If 2H is equipped, Block Offhand?
                                const isBlocked = slotDef.id === 'offHand' && !!twoHandItem;
                                // If 2H is equipped, SHOW it in Offhand too (dimmed or translucent)
                                const ghostItem = isBlocked ? twoHandItem : null;

                                return (
                                    <Droppable key={slotDef.id} droppableId={`equipped-${slotDef.id}`} isDropDisabled={isBlocked}>
                                        {(provided, snapshot) => (
                                            <div 
                                                className={`${styles.equipSlot} ${slotDef.css} ${snapshot.isDraggingOver && !isBlocked ? styles.validDrop : ''} ${isBlocked ? styles.blockedSlot : ''} ${validDragSlots.includes(slotDef.id) && !isBlocked ? styles.validTarget : ''}`}
                                                ref={provided.innerRef}
                                                {...provided.droppableProps}
                                            >
                                                {displayItem ? (
                                                     <Draggable key={`${displayItem.id}-${slotDef.id}`} draggableId={`${displayItem.id}-${slotDef.id}`} index={0}>
                                                        {(dragProvided, dragSnapshot) => (
                                                            <div
                                                                ref={dragProvided.innerRef}
                                                                {...dragProvided.draggableProps}
                                                                {...dragProvided.dragHandleProps}
                                                                style={{width: '100%', height: '100%', ...dragProvided.draggableProps.style}}
                                                                onDoubleClick={() => handleDoubleClick(displayItem)}
                                                            >
                                                                <Tooltip itemId={displayItem.id} socketedRunes={socketedRunes.filter(sr => sr.itemInstanceId != null ? sr.itemInstanceId === displayItem.itemInstanceId : sr.equipmentSlot === displayItem.slot)}>
                                                                    <img 
                                                                        src={getItemImageSrc(displayItem)} 
                                                                        alt={displayItem.name} 
                                                                        className={styles.itemIcon} 
                                                                        onError={handleImgError}
                                                                    />
                                                                </Tooltip>
                                                            </div>
                                                        )}
                                                     </Draggable>
                                                ) : ghostItem ? (
                                                    <div className={`${styles.itemContainer} ${styles.ghostItem}`}>
                                                         <img 
                                                            src={getItemImageSrc(ghostItem)} 
                                                            alt={ghostItem.name} 
                                                            className={styles.itemIcon}
                                                            style={{opacity: 0.5, filter: 'grayscale(100%)'}} 
                                                            onError={handleImgError}
                                                        />
                                                    </div>
                                                ) : null}
                                                
                                                <span className={styles.equipSlotLabel}>{slotDef.label}</span>

                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                );
                            })}
                        </div>

                         {/* Active Potions Widget at bottom of Char Sheet */}
                        <div className={styles.potionsWidget}>
                            <div className={styles.potionsTitle}>Active Effects</div>
                            {activePotions.length > 0 ? (
                                activePotions.map((p, i) => (
                                    <div key={i} className={styles.activePotionRow} data-tooltip={p.effect}>
                                        <span>{p.name}</span>
                                        <span className={styles.timer}>{p.hours}:{p.minutes < 10 ? `0${p.minutes}` : p.minutes}:{p.seconds < 10 ? `0${p.seconds}` : p.seconds}</span>
                                    </div>
                                ))
                            ) : <div style={{fontSize: '0.8rem', color: '#666'}}>No active potions</div>}
                        </div>
                    </div>

                    </div>{/* END equipmentGroup */}

                    {/* RIGHT: Inventory Bag */}
                    <div className={styles.inventoryPanel}>
                        <div className={styles.tabsContainer}>
                            {['All', 'Gear', 'Consumables', 'Materials'].map(tab => (
                                <button 
                                    key={tab} 
                                    className={`${styles.tab} ${activeTab === tab ? styles.active : ''}`}
                                    onClick={() => setActiveTab(tab)}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>

                        <Droppable droppableId="inventory-bag">
                            {(provided, snapshot) => (
                                <div 
                                    className={styles.inventoryGrid}
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                >
                                    {bagItems.length === 0 && (
                                        <div className={styles.emptyMessage}>Inventory is empty</div>
                                    )}
                                    
                                    {bagItems.map((item, index) => {
                                        const dragKey = item.itemInstanceId
                                          ? `${item.id}-inst${item.itemInstanceId}-inventory`
                                          : `${item.id}-inventory`;
                                        return (
                                        <Draggable key={dragKey} draggableId={dragKey} index={index}>
                                            {(dragProvided, dragSnapshot) => (
                                                 <div 
                                                    className={`${styles.bagSlot} ${getRarityClass(item)}`}
                                                    ref={dragProvided.innerRef}
                                                    {...dragProvided.draggableProps}
                                                    {...dragProvided.dragHandleProps}
                                                    onDoubleClick={() => handleDoubleClick(item)}
                                                 >
                                                    <Tooltip itemId={item.id} socketedRunes={socketedRunes.filter(sr => item.itemInstanceId ? sr.itemInstanceId === item.itemInstanceId : (sr.itemId === item.id && !sr.isEquipped))}>
                                                        <img 
                                                            src={getItemImageSrc(item)} 
                                                            alt={item.name} 
                                                            className={item.itemCategory === 'Rune' ? styles.runeIcon : styles.itemIcon}
                                                            onError={handleImgError}
                                                        />
                                                        {((item.quantity ?? 0) > 1 || item.itemCategory === 'Material') &&
                                                            <span className={styles.stackCount}>{item.quantity ?? 1}</span>
                                                        }
                                                    </Tooltip>
                                                    
                                                    {/* Double-click hint */}
                                                 </div>
                                            )}
                                        </Draggable>
                                        );
                                    })}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                        
                        {/* Footer context hint */}
                        <div style={{padding: '10px', fontSize: '0.8rem', color: '#666', textAlign: 'center'}}>
                            Hint: Double-click items to equip/use, or drag them to slots.
                        </div>
                    </div>

                </div>
            </DragDropContext>
        )}

        <Modal
          show={runeSocketConfirm !== null}
          title={runeSocketConfirm?.replacingRuneName ? 'Replace Rune' : 'Socket Rune'}
          message={
            runeSocketConfirm && (
              runeSocketConfirm.replacingRuneName ? (
                <>
                  <p>Replace <strong>{runeSocketConfirm.replacingRuneName}</strong> with <strong>{runeSocketConfirm.runeName}</strong> in <strong>{runeSocketConfirm.targetName}</strong>?</p>
                  <p style={{ color: '#e74c3c', marginTop: '0.5rem' }}>⚠ <strong>{runeSocketConfirm.replacingRuneName}</strong> will be <strong>destroyed</strong>. This cannot be undone!</p>
                </>
              ) : (
                <>
                  <p>Socket <strong>{runeSocketConfirm.runeName}</strong> into <strong>{runeSocketConfirm.targetName}</strong>?</p>
                  <p style={{ color: '#e74c3c', marginTop: '0.5rem' }}>⚠ This is <strong>permanent</strong> and cannot be undone.</p>
                </>
              )
            )
          }
          onClose={() => setRuneSocketConfirm(null)}
          onConfirm={() => {
            if (runeSocketConfirm) {
              executeSocketRune(
                runeSocketConfirm.runeItemId,
                runeSocketConfirm.itemInstanceId,
                runeSocketConfirm.runeSlotIndex,
                !!runeSocketConfirm.replacingRuneName
              );
              setRuneSocketConfirm(null);
            }
          }}
        />
    </div>
  );
};

export default Inventory;
